import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Warehouse,
  PackageSearch,
  ClipboardList,
  ArrowRightLeft,
  Cylinder,
  Activity,
  ArrowRight,
} from 'lucide-react';
import { yardApi, YardDashboard as YardDashboardData } from '@/api/services/yard.api';
import { toast } from 'sonner';
import { PageHeader, StatusBadge, MetricCard, KPIGrid } from '@/components/common';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

export default function YardDashboard() {
  const [dashboard, setDashboard] = useState<YardDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const data = await yardApi.getDashboard();
      setDashboard(data);
    } catch (error) {
      console.error('Error fetching yard dashboard:', error);
      toast.error('Failed to load yard dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Yard Dashboard"
          description="Current yard position across storage occupancy, open tasks, and kits in assembly."
          actions={
            <Link to="/yard/asset-search">
              <Button>
                <PackageSearch className="h-4 w-4 mr-2" />
                Search Assets
              </Button>
            </Link>
          }
        />
      </motion.div>

      {/* Quick Stats */}
      <motion.div variants={fadeUp}>
        <KPIGrid columns={4} className="lg:grid-cols-5">
          <MetricCard
            label="Storage Locations"
            value={stats?.storage.totalLocations || 0}
            sentiment="info"
            icon={<Warehouse className="h-5 w-5" />}
            subtitle={`${stats?.storage.byStatus.AVAILABLE || 0} available`}
          />
          <MetricCard
            label="Putaway Tasks"
            value={stats?.putaway.pending || 0}
            sentiment="warning"
            icon={<ArrowRightLeft className="h-5 w-5" />}
            subtitle={`${stats?.putaway.inProgress || 0} in progress`}
          />
          <MetricCard
            label="Job Kits"
            value={stats?.jobKits.ready || 0}
            sentiment="info"
            icon={<ClipboardList className="h-5 w-5" />}
            subtitle={`${stats?.jobKits.pendingPick || 0} pending pick`}
          />
          <MetricCard
            label="Pick Lists"
            value={stats?.picking.pending || 0}
            sentiment="success"
            icon={<Activity className="h-5 w-5" />}
            subtitle={`${stats?.picking.completedToday || 0} completed today`}
          />
          <MetricCard
            label="Pipe Inventory"
            value={stats?.pipeTally.totalJoints || 0}
            sentiment="neutral"
            icon={<Cylinder className="h-5 w-5" />}
            subtitle={`${stats?.pipeTally.totalFootage?.toLocaleString() || 0} ft`}
          />
        </KPIGrid>
      </motion.div>

      {/* Quick Actions */}
      <motion.div variants={fadeUp}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/yard/locations">
            <Card className="bg-card hover:bg-muted transition-colors cursor-pointer">
              <CardContent className="pt-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Warehouse className="h-5 w-5 text-neutral-500" />
                  <span className="font-medium">Storage Locations</span>
                </div>
                <ArrowRight className="h-4 w-4 text-neutral-400" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/yard/putaway">
            <Card className="bg-card hover:bg-muted transition-colors cursor-pointer">
              <CardContent className="pt-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ArrowRightLeft className="h-5 w-5 text-neutral-500" />
                  <span className="font-medium">Putaway Tasks</span>
                </div>
                <Badge variant="secondary">{stats?.putaway.pending || 0}</Badge>
              </CardContent>
            </Card>
          </Link>

          <Link to="/yard/job-kits">
            <Card className="bg-card hover:bg-muted transition-colors cursor-pointer">
              <CardContent className="pt-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ClipboardList className="h-5 w-5 text-neutral-500" />
                  <span className="font-medium">Job Kits</span>
                </div>
                <Badge variant="secondary">{stats?.jobKits.ready || 0} ready</Badge>
              </CardContent>
            </Card>
          </Link>

          <Link to="/yard/pipe-tally">
            <Card className="bg-card hover:bg-muted transition-colors cursor-pointer">
              <CardContent className="pt-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Cylinder className="h-5 w-5 text-neutral-500" />
                  <span className="font-medium">Pipe Tally</span>
                </div>
                <ArrowRight className="h-4 w-4 text-neutral-400" />
              </CardContent>
            </Card>
          </Link>
        </div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div variants={fadeUp}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Putaway Tasks */}
          <Card>
            <CardHeader>
              <h3 className="text-base font-medium text-foreground">
                <ArrowRightLeft className="h-4 w-4" />
                Recent Putaway
              </h3>
            </CardHeader>
            <CardContent className="space-y-3">
              {dashboard?.recentActivity.putaways.length === 0 ? (
                <p className="text-sm text-neutral-500">No recent putaway tasks</p>
              ) : (
                dashboard?.recentActivity.putaways.slice(0, 5).map((task) => (
                  <div key={task.id} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium">{task.item.name}</p>
                      <p className="text-neutral-500">{task.taskNumber}</p>
                    </div>
                    <StatusBadge status={task.status} />
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Recent Job Kits */}
          <Card>
            <CardHeader>
              <h3 className="text-base font-medium text-foreground">
                <ClipboardList className="h-4 w-4" />
                Recent Job Kits
              </h3>
            </CardHeader>
            <CardContent className="space-y-3">
              {dashboard?.recentActivity.jobKits.length === 0 ? (
                <p className="text-sm text-neutral-500">No recent job kits</p>
              ) : (
                dashboard?.recentActivity.jobKits.slice(0, 5).map((kit) => (
                  <div key={kit.id} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium">{kit.name}</p>
                      <p className="text-neutral-500">{kit.kitNumber}</p>
                    </div>
                    <StatusBadge status={kit.status} />
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Recent Pick Lists */}
          <Card>
            <CardHeader>
              <h3 className="text-base font-medium text-foreground">
                <Activity className="h-4 w-4" />
                Recent Pick Lists
              </h3>
            </CardHeader>
            <CardContent className="space-y-3">
              {dashboard?.recentActivity.pickLists.length === 0 ? (
                <p className="text-sm text-neutral-500">No recent pick lists</p>
              ) : (
                dashboard?.recentActivity.pickLists.slice(0, 5).map((list) => (
                  <div key={list.id} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium">{list.pickListNumber}</p>
                      <p className="text-neutral-500">{list.sourceType}</p>
                    </div>
                    <StatusBadge status={list.status} />
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </motion.main>
  );
}
